import Button, { buttonClasses } from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { type FC } from "react";
import { AssignedVcAccount } from "./vc-account-assignments-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteVcAccountAssignment } from "./vc-account-assignments-api";
import { vcAccountAssignments } from "src/queries/vc-account-assignments";
import toast from "react-hot-toast";
import PulseLoader from "react-spinners/PulseLoader";

export interface DeleteVcAccountAssignmentDialogProps {
  isOpen: boolean;
  vcAccount: AssignedVcAccount | null;
  onClose(): void;
}

export const DeleteVcAccountAssignmentDialog: FC<DeleteVcAccountAssignmentDialogProps> = ({
  isOpen,
  vcAccount,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const deleteAssignmentMutation = useMutation({
    mutationKey: ["vc-user-assignments", "delete"],
    mutationFn: deleteVcAccountAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vcAccountAssignments._def });
      toast.success("VC account assignment deleted");
      onClose();
    },
  });

  return (
    <Dialog open={isOpen} onClose={deleteAssignmentMutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      {vcAccount && (
        <>
          <DialogTitle sx={{ border: "none" }}>Cancel VC Account Assignment</DialogTitle>
          <div style={{ paddingInline: "1.5rem", paddingTop: 0, paddingBottom: "1rem" }}>
            <p style={{ marginBottom: "1rem" }}>Are you sure you want to cancel this assignment?</p>
            <ul style={{ marginBottom: "1rem" }}>
              <li>Email: {vcAccount.email}</li>
              <li>Store: {vcAccount.storeName}</li>
              {vcAccount.notes && <li>Notes: {vcAccount.notes}</li>}
            </ul>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
              <Button type="button" variant="outlined" onClick={onClose} disabled={deleteAssignmentMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={deleteAssignmentMutation.isPending ? "outlined" : "contained"}
                color="primary"
                disabled={deleteAssignmentMutation.isPending}
                startIcon={deleteAssignmentMutation.isPending ? <PulseLoader size="8px" color="#777" /> : undefined}
                sx={
                  deleteAssignmentMutation.isPending
                    ? { [`&.${buttonClasses.disabled}`]: { color: "#444" } }
                    : undefined
                }
                onClick={() => deleteAssignmentMutation.mutate({ vcAccountIds: [vcAccount.avcAccountCredentialsId] })}
              >
                {deleteAssignmentMutation.isPending ? (
                  <>Cancelling account assignment...</>
                ) : (
                  <>Cancel account assignment</>
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};
